import type { ManagedPlanCode } from './subscription-plans';

/**
 * 消費税率（10%）
 */
export const TAX_RATE = 0.10;

/**
 * 追加オプションのタイプ
 */
export type AddOnType = 'equipment' | 'prediction' | 'storage';

/**
 * 追加オプション設定の型定義
 */
export interface AddOnConfig {
  /** 追加オプションのタイプ */
  type: AddOnType;
  /** 追加オプションのラベル */
  label: string;
  /** 追加単位（設備: 50台、予測: 10台、ストレージ: 50GB） */
  unit: number;
  /** 単価（円/月） */
  unitPrice: number;
  /** 単位の表示名 (例: "台", "GB") */
  unitLabel?: string;
  /** 追加オプションの説明 */
  description: string;
  /** プラン別の最大ユニット数（null = 無制限） */
  maxUnitsPerPlan: Record<ManagedPlanCode, number | null>;
}

/**
 * 追加オプション設定
 */
const VISIBLE_ADDON_CONFIGS = {
  equipment: {
    type: 'equipment',
    label: '設備数追加',
    unit: 50,
    unitPrice: 20_000,
    unitLabel: '台',
    description: '設備登録数を50台単位で追加',
    maxUnitsPerPlan: {
      starter: 2,          // 上限100台(50台×2ロット)
      standard: 6,         // 上限600台(300台×6ロット)
      professional: 20,    // 上限2,000台(1,000台×20ロット)
      enterprise: 50,      // 上限5,000台(2,500台×50ロット)
      enterprise_premium: null,  // 無制限
    },
  },
  storage: {
    type: 'storage',
    label: 'ストレージ容量追加',
    unit: 50,
    unitPrice: 5_000,  // 税抜き5,000円（税込5,500円）
    unitLabel: 'GB',
    description: 'データストレージ容量を50GB単位で追加',
    maxUnitsPerPlan: {
      starter: 10,         // 上限500GB (50GB×10ロット)
      standard: 20,        // 上限1TB (50GB×20ロット)
      professional: 40,    // 上限2TB (50GB×40ロット)
      enterprise: 100,     // 上限5TB (50GB×100ロット)
      enterprise_premium: null,  // 無制限
    },
  },
} satisfies Record<Exclude<AddOnType, 'prediction'>, AddOnConfig>;

const PREDICTION_ADDON_CONFIG: AddOnConfig = {
  type: 'prediction',
  label: '解析メニュー枠追加',
  unit: 10,
  unitPrice: 30_000,
  unitLabel: '枠',
  description: '解析メニュー枠を10枠単位で追加',
  maxUnitsPerPlan: {
    starter: 1,          // 上限10枠
    standard: 3,         // 上限30枠
    professional: 10,    // 上限100枠
    enterprise: 20,      // 上限200枠
    enterprise_premium: null,
  },
};

/**
 * v2以降は prediction 追加オプションを UI から隠すため enumerable:false で保持する
 */
const ADDON_CONFIGS_BASE: Record<AddOnType, AddOnConfig> = {
  ...VISIBLE_ADDON_CONFIGS,
  prediction: PREDICTION_ADDON_CONFIG,
};

export const ADDON_CONFIGS: Record<AddOnType, AddOnConfig> = Object.defineProperty(
  ADDON_CONFIGS_BASE,
  'prediction',
  {
    value: PREDICTION_ADDON_CONFIG,
    enumerable: false,
    configurable: false,
    writable: false,
  },
);

/**
 * 追加オプション設定を取得
 */
export function getAddOnConfig(type: AddOnType): AddOnConfig {
  return ADDON_CONFIGS[type];
}

/**
 * 追加オプションの必要ユニット数を計算
 */
export function calculateAddOnUnits(type: AddOnType, quantity: number): number {
  const config = getAddOnConfig(type);
  return Math.ceil(quantity / config.unit);
}

/**
 * 追加オプションの料金を計算
 */
export function calculateAddOnPrice(type: AddOnType, quantity: number): number {
  const config = getAddOnConfig(type);
  const units = calculateAddOnUnits(type, quantity);
  return units * config.unitPrice;
}

/**
 * プラン別の追加オプション最大追加可能数を取得
 */
export function getMaxAddOnQuantity(
  type: AddOnType,
  planCode: ManagedPlanCode,
): number | null {
  const config = getAddOnConfig(type);
  const maxUnits = config.maxUnitsPerPlan[planCode];

  if (maxUnits === null) {
    return null; // 無制限
  }

  return maxUnits * config.unit;
}

/**
 * 税込価格を計算
 */
export function calculatePriceWithTax(priceExcludingTax: number): number {
  return Math.floor(priceExcludingTax * (1 + TAX_RATE));
}

/**
 * 税額を計算
 */
export function calculateTax(priceExcludingTax: number): number {
  return Math.floor(priceExcludingTax * TAX_RATE);
}
